// web/src/routes/board/Daily.jsx
// The Daily Board — the killer screen.
// Mobile-first: tasks grouped by time block, big tap-targets to check off,
// tap again to undo. Dates stay barn-local (the server resolves "today").

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import AddTaskModal from '../../components/board/AddTaskModal';
import { fmtDate } from '../../lib/dates';

const ORDER = [
  ['am', 'Morning'],
  ['noon', 'Midday'],
  ['pm', 'Afternoon'],
  ['nc', 'Night check'],
];
const KNOWN_BLOCKS = ORDER.map(([id]) => id);

// POST /tasks is owner/manager-only, so only they get the add affordance.
const ADD_ROLES = ['owner', 'manager'];

export default function DailyBoard() {
  const qc = useQueryClient();
  const { activeRole } = useAuth();
  const canAdd = ADD_ROLES.includes(activeRole);
  const [date, setDate] = useState(null); // null = server-resolved "today"

  // ?new=1 opens the form straight away — that's how the dashboard's "+ Task"
  // quick action gets here.
  const [searchParams, setSearchParams] = useSearchParams();
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (searchParams.get('new') === null) return;
    if (canAdd) setAdding(true);
    // Drop the param so a refresh (or a back-nav) doesn't reopen the form.
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canAdd]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tasks', date ?? 'today'],
    queryFn: () => api.get(date ? `/tasks?date=${date}` : '/tasks'),
    retry: false,
  });

  // Lock onto a concrete date once the server tells us today (enables stepping).
  useEffect(() => {
    if (date === null && data?.date) setDate(data.date);
  }, [date, data]);

  const toggle = useMutation({
    mutationFn: ({ id, done }) =>
      done ? api.patch(`/tasks/${id}`, { status: 'pending' }) : api.post(`/tasks/${id}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  if (isError && error?.status === 403) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">
          The daily board is for barn staff.
        </div>
      </div>
    );
  }

  const tasks = data?.tasks ?? [];
  const tracked = tasks.filter((t) => t.status === 'pending' || t.status === 'done');
  const doneCount = tracked.filter((t) => t.status === 'done').length;
  const viewDate = date ?? data?.date ?? null;
  const isToday = Boolean(data?.today && viewDate === data.today);

  return (
    <div className="max-w-2xl mx-auto pb-24 sm:pb-8">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-ink-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">{isToday ? 'Today' : 'Schedule'}</h1>
          <div className="flex items-center gap-3">
            {tracked.length > 0 && (
              <span className="text-sm text-ink-500 tabular-nums">
                {doneCount}/{tracked.length} done
              </span>
            )}
            {canAdd && (
              <button onClick={() => setAdding(true)} className="btn-secondary text-sm">
                + Task
              </button>
            )}
          </div>
        </div>
        <DateStepper
          viewDate={viewDate}
          isToday={isToday}
          onPrev={() => setDate(addDaysISO(viewDate, -1))}
          onNext={() => setDate(addDaysISO(viewDate, +1))}
          onToday={() => setDate(data?.today ?? null)}
        />
      </header>

      <div className="px-4 pt-4">
        {isLoading ? (
          <Skeleton />
        ) : isError ? (
          <p className="text-red-600 text-sm">Couldn’t load the board. Try again.</p>
        ) : tasks.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-ink-500">Nothing scheduled for this day.</p>
            {canAdd && (
              <button onClick={() => setAdding(true)} className="btn-primary mt-4">
                + Add a Task
              </button>
            )}
          </div>
        ) : (
          buildGroups(tasks).map((g) => (
            <section key={g.label} className="mb-6">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-400 mb-2">
                {g.label}
              </h2>
              <div className="space-y-2">
                {g.items.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    busy={toggle.isPending}
                    onToggle={() => toggle.mutate({ id: t.id, done: t.status === 'done' })}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {adding && (
        <AddTaskModal
          date={viewDate}
          onClose={() => setAdding(false)}
          // Jump the board to the day the task landed on, so it's visible.
          onCreated={(savedDate) => savedDate && setDate(savedDate)}
        />
      )}
    </div>
  );
}

function buildGroups(tasks) {
  const groups = ORDER.map(([id, label]) => ({
    label,
    items: tasks.filter((t) => t.timeBlock === id),
  }));
  const rest = tasks.filter((t) => !KNOWN_BLOCKS.includes(t.timeBlock));
  if (rest.length) groups.push({ label: 'Anytime', items: rest });
  return groups.filter((g) => g.items.length > 0);
}

function TaskRow({ task, onToggle, busy }) {
  const done = task.status === 'done';
  const muted = task.status === 'skipped' || task.status === 'cancelled';
  return (
    <div
      className={
        'flex items-center gap-3 p-4 rounded-xl border transition ' +
        (done ? 'bg-ink-50 border-ink-100' : 'bg-white border-ink-100')
      }
    >
      <button
        onClick={onToggle}
        disabled={busy || muted}
        aria-pressed={done}
        aria-label={done ? 'Mark not done' : 'Mark complete'}
        className={
          'w-12 h-12 rounded-full border-2 flex items-center justify-center text-lg transition shrink-0 ' +
          (done
            ? 'bg-emerald-500 border-emerald-500 text-white'
            : 'border-ink-200 hover:border-emerald-500 active:scale-95')
        }
      >
        {done ? '✓' : ''}
      </button>

      <div className="flex-1 min-w-0">
        <div
          className={
            'font-medium truncate ' + (done ? 'text-ink-400 line-through' : muted ? 'text-ink-400' : '')
          }
        >
          {task.horseName ?? 'Barn-wide'}
          {task.stall && <span className="text-ink-400 text-sm ml-1">· stall {task.stall}</span>}
        </div>
        <div className="text-sm text-ink-500 truncate">{labelForTask(task)}</div>
      </div>

      {task.time && <span className="text-xs text-ink-400 tabular-nums shrink-0">{task.time}</span>}

      {task.horseId && (
        <Link
          to={`/horses/${task.horseId}`}
          className="text-ink-300 px-1 text-xl shrink-0"
          aria-label="Horse details"
        >
          ›
        </Link>
      )}
    </div>
  );
}

function DateStepper({ viewDate, isToday, onPrev, onNext, onToday }) {
  return (
    <div className="flex items-center justify-between mt-3">
      <button
        onClick={onPrev}
        className="w-9 h-9 rounded-md border border-ink-100 text-ink-600 text-lg active:scale-95"
        aria-label="Previous day"
      >
        ‹
      </button>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{formatDate(viewDate)}</span>
        {isToday ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Today</span>
        ) : (
          <button onClick={onToday} className="text-xs text-ink-500 underline">
            Today
          </button>
        )}
      </div>
      <button
        onClick={onNext}
        className="w-9 h-9 rounded-md border border-ink-100 text-ink-600 text-lg active:scale-95"
        aria-label="Next day"
      >
        ›
      </button>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-20 rounded-xl bg-white border border-ink-100 animate-pulse" />
      ))}
    </div>
  );
}

function labelForTask(task) {
  const parts = [task.taskType];
  if (task.rideType) parts.push(task.rideType);
  if (task.assignedName) parts.push(`· ${task.assignedName}`);
  return parts.filter(Boolean).join(' ');
}

// Format an ISO date (YYYY-MM-DD) as "Sat, May 30" without timezone drift.
function formatDate(iso) {
  if (!iso) return '';
  return fmtDate(iso, 'dayShort');
}

// Pure ISO date arithmetic (no timezone needed — operates on the date string).
function addDaysISO(iso, n) {
  const base = iso ?? new Date().toISOString().slice(0, 10);
  const d = new Date(base + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
